import {
	Bleed,
	Box,
	Button,
	Dialog,
	Em,
	Flex,
	Heading,
	HStack,
	List,
	Stack,
	Text,
} from '@welbe/design-system';
import { InputFile, Show, tokens } from '@welbe/web-design-system';
import { Preview } from '../Preview/Preview';
import {
	generateLinkToUploadEmployees,
	generateLinkToUploadResignationEmployees,
} from 'services/services';
import axios from 'axios';
import { useState } from 'react';
import { onDownloadCSVTemplate } from 'utils/downloadCSVTemplate';
import {
	addUpload,
	cleanOldUploads,
	getUploadByName,
	updateUpload,
} from 'Views/Employees/db';
import { Auth } from 'aws-amplify';
import { FiInfo } from 'react-icons/fi';

type UploadComponentProps = {
	type: 'addEmployees' | 'resignationEmployees';
	rows: string[][];
	onClose: () => void;
	onSuccess: () => void;
};

export const UploadComponent = ({
	type,
	rows,
	onClose,
	onSuccess,
}: UploadComponentProps) => {
	const [confirmUpload, setConfirmUpload] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [files, setFiles] = useState<File[]>([]);

	const handleSelectFile = (selectedFile: FileList | File | null) => {
		if (selectedFile === null) return;
		if (selectedFile instanceof File) {
			setErrorMessage(
				selectedFile?.type === 'text/csv'
					? ''
					: '¡Archivo no soportado! Por favor, inténtelo de nuevo.',
			);
			setFiles([selectedFile]);
		}
	};

	const handleConfirmSubmit = () => {
		setConfirmUpload(false);
		handleSubmit(true);
	};

	const handleSubmit = async (bypass?: boolean) => {
		const [file] = files;
		try {
			setLoading(true);
			await cleanOldUploads();
			const existing = await getUploadByName(file.name);

			if (existing && !bypass) {
				setConfirmUpload(true);
				return;
			}

			const uploadEndpoint =
				type === 'addEmployees'
					? generateLinkToUploadEmployees
					: generateLinkToUploadResignationEmployees;

			const { data } = await uploadEndpoint();

			await axios.put(data.content.url, file, {
				headers: { 'Content-Type': file.type },
			});

			const currentUser = await Auth.currentUserInfo();

			bypass
				? await updateUpload(file.name, Date.now())
				: await addUpload({
						fileName: file.name,
						type: type,
						user: currentUser.username,
						timestamp: Date.now(),
				  });

			onSuccess();
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadCSV = async () => {
		onDownloadCSVTemplate(rows, 'plantillas');
	};

	return (
		<Show
			when={!confirmUpload}
			fallback={
				<Dialog.Body padding="2rem">
					<Stack alignItems="center">
						<Box backgroundColor="orange.50" borderRadius="l3" padding="0.5rem">
							<FiInfo color="orange" size={36} />
						</Box>
						<Heading size="lg" color="gray.700">
							Archivo ya procesado
						</Heading>
						<Text width="80%">
							En menos de 5 minutos se ha procesado un archivo con el nombre{' '}
							<strong>{files[0]?.name}</strong>. ¿Desea continuar?
						</Text>
						<Flex justifyContent="space-around" gap="2rem" mt="2rem">
							<Button size="sm" onClick={() => onClose()} variant="outline">
								Cancelar
							</Button>
							<Button size="sm" onClick={handleConfirmSubmit}>
								Confirmar
							</Button>
						</Flex>
					</Stack>
				</Dialog.Body>
			}
		>
			<>
				<Dialog.Header padding="2rem">
					<Dialog.Title
						width="100%"
						fontSize="1.125rem"
						fontWeight="600"
						color={tokens.color_primary}
						textAlign="center"
					>
						Subir Archivo
					</Dialog.Title>
				</Dialog.Header>
				<Dialog.Body padding="0 2rem 2rem 3rem">
					<List.Root
						as="ol"
						gap="0.5rem"
						mb="0.5rem"
						fontSize="1rem"
						fontWeight="600"
						color={tokens.color_gray_dark}
					>
						<List.Item>
							Descargar la plantilla.csv
							<Stack gap="0.25rem" mt="1rem" marginY="1rem">
								<Button variant="outline" size="sm" onClick={handleDownloadCSV}>
									{type === 'addEmployees'
										? 'Alta de colaboradores.csv'
										: 'Baja de colaboradores.csv'}
								</Button>
								<Text
									fontSize="0.75rem"
									fontWeight="400"
									color={tokens.color_gray_dark}
								>
									Los identificadores ID están dentro del archivo csv.
								</Text>
							</Stack>
						</List.Item>
						<List.Item>
							Llena cuidadosamente el formato
							<Bleed height="3rem" />
						</List.Item>
						<List.Item>
							Sube el archivo en formato.csv
							<Stack gap="0.25rem" marginY="1rem">
								<Text
									fontSize="0.75rem"
									fontWeight="400"
									color={tokens.color_gray_dark}
								>
									Deve ser permitido apenas arquivo com a extensão.csv
								</Text>
								<Show
									when={!files?.length}
									fallback={
										<Preview
											files={files}
											onChangeFiles={setFiles}
											onRemoveFile={() => setFiles([])}
										/>
									}
								>
									<InputFile
										placeholder="Subir archivo"
										accept=".csv"
										onChange={(files: FileList | File | null) =>
											handleSelectFile(files)
										}
									/>
								</Show>
								<Em
									display={errorMessage && files.length ? 'block' : 'none'}
									color={tokens.color_danger}
									fontStyle="normal"
									fontSize="12px"
									fontWeight="400"
								>
									{errorMessage}
								</Em>
							</Stack>
						</List.Item>
					</List.Root>
				</Dialog.Body>
				<Dialog.Footer padding="0 2rem 2rem 2rem">
					<HStack justifyContent="space-evenly" width="100%">
						<Button
							size="sm"
							width="40%"
							variant="ghost"
							disabled={isLoading}
							onClick={() => {
								onClose();
								setFiles([]);
								setErrorMessage('');
							}}
						>
							Cancelar
						</Button>
						<Button
							size="sm"
							width="40%"
							onClick={() => handleSubmit(false)}
							loading={isLoading}
							disabled={Boolean(errorMessage) || isLoading}
						>
							Continuar
						</Button>
					</HStack>
				</Dialog.Footer>
			</>
		</Show>
	);
};
